'use client';

import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useRouter, usePathname } from '@/i18n/routing';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { Lock, User, AlertCircle, Globe, ArrowLeft, ArrowRight, ShieldCheck, ClipboardCheck, GraduationCap, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

export default function LoginPage() {
  const t = useTranslations('Auth');
  const tCommon = useTranslations('Common');
  const { login } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();

  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('password');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickLoadingRole, setQuickLoadingRole] = useState<string | null>(null);
  const [showDemoLogins, setShowDemoLogins] = useState(false);

  const toggleLanguage = () => {
    const nextLocale = locale === 'ar' ? 'en' : 'ar';
    router.replace(pathname, { locale: nextLocale });
  };

  const handleLoginProcess = async (userToLogin: string, passToLogin: string) => {
    setError(null);
    setIsSubmitting(true);

    try {
      await login(userToLogin, passToLogin);
      // Clean navigation ensuring fresh context
      if (typeof window !== 'undefined') {
        window.location.href = `/${locale}/dashboard`;
      } else {
        router.push('/dashboard');
      }
    } catch (err: any) {
      console.error('Login submission failed:', err);
      setError(err?.data?.message || err?.message || t('invalidCredentials'));
      setIsSubmitting(false);
      setQuickLoadingRole(null);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    await handleLoginProcess(username, password);
  };

  const handleQuickLogin = async (selectedUser: string, selectedPass: string, roleKey: string) => {
    setUsername(selectedUser);
    setPassword(selectedPass);
    setQuickLoadingRole(roleKey);
    await handleLoginProcess(selectedUser, selectedPass);
  };

  const demoAccounts = [
    {
      key: 'admin',
      username: 'admin',
      password: 'password',
      title: t('adminRole'),
      subtitle: t('adminDesc'),
      icon: ShieldCheck,
      color: 'border-[#0F1F3D] text-[#0F1F3D] bg-[#0F1F3D]/5 hover:bg-[#0F1F3D] hover:text-white',
      badgeBg: 'bg-[#0F1F3D] text-white',
    },
    {
      key: 'staff1',
      username: 'staff1',
      password: 'password',
      title: t('staffRole'),
      subtitle: t('staffDesc'),
      icon: ClipboardCheck,
      color: 'border-[#1E3A63] text-[#1E3A63] bg-[#1E3A63]/5 hover:bg-[#1E3A63] hover:text-white',
      badgeBg: 'bg-[#1E3A63] text-white',
    },
    {
      key: 'teacher1',
      username: 'teacher1',
      password: 'password',
      title: t('teacherRole'),
      subtitle: t('teacherDesc'),
      icon: GraduationCap,
      color: 'border-[#C9962C] text-[#C9962C] bg-[#C9962C]/5 hover:bg-[#C9962C] hover:text-white',
      badgeBg: 'bg-[#C9962C] text-[#0F1F3D]',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex flex-col justify-between p-4 sm:p-8 relative overflow-hidden">
      {/* Subtle Background Geometric Accent (Brand Navy & Gold Palette) */}
      <div className="absolute top-0 start-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-radial from-[#0F1F3D]/4 via-transparent to-transparent pointer-events-none -z-10" />

      {/* Top Header Bar */}
      <div className="flex justify-between items-center max-w-5xl w-full mx-auto">
        <div className="flex items-center gap-3">
          <Image
            src="/logo-shield-v3.png"
            alt="Al Naser School Shield"
            width={38}
            height={42}
            className="w-auto h-10 object-contain"
            priority
          />
          <div className="hidden sm:flex flex-col text-start">
            <span className="text-[#0F1F3D] font-bold text-xs tracking-tight">
              {tCommon('schoolName')}
            </span>
            <span className="text-[10px] text-[#C9962C] font-semibold tracking-wider uppercase">
              AL NASER PRIVATE SCHOOL
            </span>
          </div>
        </div>

        {/* Language Switcher */}
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white hover:bg-slate-50 text-[#0F1F3D] text-xs font-semibold shadow-xs border border-[#E5E7EB] hover:border-[#C9962C]/50 transition cursor-pointer"
        >
          <Globe className="w-3.5 h-3.5 text-[#C9962C]" />
          <span>{locale === 'ar' ? 'English' : 'العربية'}</span>
        </button>
      </div>

      {/* Standout Centered Card */}
      <div className="max-w-md w-full mx-auto my-auto bg-white rounded-3xl shadow-xl shadow-slate-200/60 p-6 sm:p-8 border border-[#E5E7EB] relative">
        {/* Top Gold Border Accent */}
        <div className="absolute top-0 inset-x-8 h-1 bg-gradient-to-r from-transparent via-[#C9962C] to-transparent rounded-t-3xl" />

        {/* Full Logo Lockup */}
        <div className="flex flex-col items-center text-center mb-5">
          <Image
            src="/logo-full.png"
            alt="مدرسة النصر الخاصة - Al Naser Private School"
            width={240}
            height={210}
            className="w-auto h-32 sm:h-36 object-contain mb-2"
            priority
          />
          <div className="w-14 h-0.5 bg-[#C9962C] rounded-full my-1"></div>
          <h1 className="text-sm font-bold text-[#0F1F3D] mt-1 tracking-tight">
            {t('title')}
          </h1>
          <p className="text-xs text-[#6B7280] mt-0.5">
            {t('subtitle')}
          </p>
        </div>

        {/* Quick Demo Login Collapsible Section */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowDemoLogins(!showDemoLogins)}
            className="w-full py-2 px-3.5 bg-gradient-to-r from-[#F8FAFC] to-[#F1F5F9] hover:from-[#F1F5F9] hover:to-[#E2E8F0] border border-[#E2E8F0] hover:border-[#0F1F3D]/20 rounded-xl flex items-center justify-between text-xs font-semibold text-[#0F1F3D] transition cursor-pointer shadow-2xs"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#C9962C]" />
              <span>{t('quickLogin')}</span>
              <span className="text-[10px] bg-[#0F1F3D]/10 text-[#0F1F3D] px-2 py-0.5 rounded-full font-medium">Demo</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#64748B] text-[11px] font-normal">
              <span>{showDemoLogins ? (locale === 'ar' ? 'إغلاق القائمة' : 'Close list') : (locale === 'ar' ? 'فتح الحسابات' : 'Open accounts')}</span>
              {showDemoLogins ? (
                <ChevronUp className="w-4 h-4 text-[#C9962C]" />
              ) : (
                <ChevronDown className="w-4 h-4 text-[#C9962C]" />
              )}
            </div>
          </button>

          {showDemoLogins && (
            <div className="mt-2.5 bg-gradient-to-br from-[#F8FAFC] to-[#F1F5F9] border border-[#E2E8F0] rounded-2xl p-3 animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-[11px] text-[#64748B] mb-2 px-0.5">{t('quickLoginDesc')}</p>
              
              <div className="grid grid-cols-1 gap-2">
                {demoAccounts.map((account) => {
                  const IconComp = account.icon;
                  const isLoadingThis = quickLoadingRole === account.key && isSubmitting;
                  return (
                    <button
                      key={account.key}
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => handleQuickLogin(account.username, account.password, account.key)}
                      className={`group relative flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer text-start bg-white hover:shadow-md ${
                        quickLoadingRole === account.key
                          ? 'border-[#0F1F3D] ring-2 ring-[#0F1F3D]/20 bg-slate-50'
                          : 'border-[#E2E8F0] hover:border-[#0F1F3D]/30'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition ${
                          account.key === 'admin' ? 'bg-[#0F1F3D] text-white' :
                          account.key === 'staff1' ? 'bg-[#1E3A63] text-white' :
                          'bg-[#C9962C] text-[#0F1F3D]'
                        }`}>
                          {isLoadingThis ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <IconComp className="w-4 h-4" />
                          )}
                        </div>
                        <div className="truncate">
                          <div className="text-xs font-bold text-[#0F1F3D] flex items-center gap-1.5">
                            <span>{account.title}</span>
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-[#475569]">
                              {account.username}
                            </span>
                          </div>
                          <div className="text-[10px] text-[#64748B] truncate mt-0.5">
                            {account.subtitle}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 text-[11px] font-semibold text-[#0F1F3D] opacity-80 group-hover:opacity-100 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 transition flex-shrink-0 ms-2">
                        {locale === 'ar' ? (
                          <ArrowLeft className="w-3.5 h-3.5 text-[#C9962C]" />
                        ) : (
                          <ArrowRight className="w-3.5 h-3.5 text-[#C9962C]" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Divider with Or (only when demo list is open) */}
        {showDemoLogins && (
          <div className="relative flex py-1 items-center">
            <div className="flex-grow border-t border-[#E5E7EB]"></div>
            <span className="flex-shrink mx-3 text-[10px] uppercase font-bold text-[#9CA3AF] tracking-wider">
              {locale === 'ar' ? 'أو تسجيل الدخول اليدوي' : 'Or manual sign in'}
            </span>
            <div className="flex-grow border-t border-[#E5E7EB]"></div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="my-3 p-3 rounded-xl bg-rose-50 border border-rose-200 flex items-start gap-3 text-[#DC2626] text-xs animate-in fade-in duration-200">
            <AlertCircle className="w-4 h-4 text-[#DC2626] flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="space-y-3 mt-2"
        >
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1 text-start">
              {t('username')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none text-[#6B7280]">
                <User className="w-4 h-4" />
              </div>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="admin"
                className="w-full ps-10 pe-4 py-2 bg-[#F7F8FA] border border-[#E5E7EB] rounded-xl text-xs text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#C9962C]/40 focus:border-[#C9962C] focus:bg-white transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1 text-start">
              {t('password')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none text-[#6B7280]">
                <Lock className="w-4 h-4" />
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="••••••••"
                className="w-full ps-10 pe-4 py-2 bg-[#F7F8FA] border border-[#E5E7EB] rounded-xl text-xs text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#C9962C]/40 focus:border-[#C9962C] focus:bg-white transition"
              />
            </div>
          </div>

          <div className="pt-1.5">
            <button
              type="button"
              onClick={() => handleSubmit()}
              disabled={isSubmitting}
              className="w-full py-2.5 px-4 bg-[#0F1F3D] hover:bg-[#1E3A63] disabled:opacity-60 text-white font-semibold rounded-xl text-xs shadow-md shadow-[#0F1F3D]/20 border border-[#0F1F3D] hover:border-[#C9962C]/40 transition flex items-center justify-center gap-2 cursor-pointer"
            >
              {isSubmitting && !quickLoadingRole ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{t('loggingIn')}</span>
                </>
              ) : (
                <>
                  <span>{t('loginButton')}</span>
                  {locale === 'ar' ? (
                    <ArrowLeft className="w-3.5 h-3.5 text-[#C9962C]" />
                  ) : (
                    <ArrowRight className="w-3.5 h-3.5 text-[#C9962C]" />
                  )}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-[#6B7280] mt-4">
        &copy; 2026 {tCommon('schoolName')} — {tCommon('appName')}
      </div>
    </div>
  );
}

